import React, {useEffect, useState} from "react";
import {useAppDispatch, useAppSelector} from "../../utils/Hooks";
import {getPerformanceIndicators, getTotal} from "../../store/reducers/PerformanceIndicatorsSlice";
import {LoggedInView} from "../common/LoggedInView";
import RangeSearch from "./components/RangeSearch";
import {useParams} from "react-router-dom";
import dayjs from "dayjs";
import {RequestStatus} from "../../utils/Enums";
import {getUsers} from "../../store/reducers/UsersSlice";
import {Tab} from "../../models/Tab";
import Totals from "./components/Totals";

const TABS: Tab[] = [
    {identifier: "total", name: "Kõik kokku"},
    {identifier: "logisticians", name: "Logistikud"},
    //{identifier: "checkers", name: "Checkerid"},
    {identifier: "customs", name: "Tollid"},
];


const EXCLUDED_LOGISTICIANS_IDS: number[] = [1];
const EXCLUDED_CHECKERS_IDS: number[] = [25, 30];

interface PerformanceIndicatorsScreenProps {
}

export const PerformanceIndicatorsScreen: React.FC<PerformanceIndicatorsScreenProps> = () => {
    const dispatch = useAppDispatch();
    const routeParams = useParams();

    const [activeTab, setActiveTab] = useState<Tab>(TABS[0]);

    const performanceIndicatorsState = useAppSelector(state => state.performanceIndicators);
    const [week, setWeek] = useState<number>(dayjs().week());
    const [user, setUser] = useState<number>(0);
    const [year, setYear] = useState<number>(dayjs().year());

    const years: number[] = Array.from({length: Math.max(dayjs().year() - 2025 + 1, 1)}, (_, i) => 2025 + i);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const weekParam = parseInt(urlParams.get("week") || `${dayjs().week()}`, 10);
        const userParam = parseInt(urlParams.get("user") || '0', 10);
        const yearParam = parseInt(urlParams.get("year") || `${dayjs().year()}`, 10);

        if (weekParam !== week) {
            setWeek(weekParam);
        }

        if (userParam !== user) {
            setUser(userParam);
        }

        if (yearParam !== year) {
            setYear(yearParam);
        }

        dispatch(getUsers());

    }, [routeParams]);

    useEffect(() => {
        dispatch(getTotal({week, year}));
    }, [week, user, year]);
    const handleYearChange = (value: number) => {
        setYear(value);

        const urlParams = new URLSearchParams(window.location.search);
        urlParams.set('year', `${value}`);

        window.history.replaceState(null, '', `${window.location.pathname}?${urlParams.toString()}`);
    };

    const filterOutExcluded = (indicators: any[], excludedIds: number[]) => {
        return indicators.filter(indicator => !excludedIds.includes(indicator.user.id));
    };

    const generateIndicatorsTable = (indicators: any[]) => {
        const filteredIndicators = filterOutExcluded(indicators, EXCLUDED_LOGISTICIANS_IDS);
        const timePercentage = performanceIndicatorsState?.total?.total?.percentage_of_year_passed || 0;

        return (
            <>
                <h3>Nädal</h3>
                <table className="data">
                    <thead>
                    <tr>
                        <th>Kasutaja</th>
                        <th>Kasumi eesmärk</th>
                        <th>Kasum</th>
                        <th>% goalist</th>
                    </tr>
                    </thead>
                    <tbody>
                    {filteredIndicators.map((indicator: any, index: number) => (
                        <tr key={index}>
                            <td>{indicator.user.firstname} {indicator.user.lastname}</td>
                            <td>{indicator.goal.toFixed(2)}</td>
                            <td>{indicator.profit.toFixed(2)}</td>
                            <td style={{ backgroundColor: indicator.profit_percentage >= 100 ? '#ccffcc' : '#ffcccc' }}>
                                {indicator.profit_percentage.toFixed(1)}%
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>

                <h3>Aasta</h3>
                <table className="data">
                    <thead>
                    <tr>
                        <th>Kasutaja</th>
                        <th>Kasumi hetkeseis</th>
                        <th>% eesmärgist</th>
                    </tr>
                    </thead>
                    <tbody>
                    {filteredIndicators.map((indicator: any, index: number) => { //annual_goal_percentage
                        const annualGoal = indicator.user?.profit_goal_annual || 0;
                        const profitThisYear = indicator.annual_profit || 0;
                        const annualGoalPercentage = Number(indicator.annual_goal_percentage?.toFixed(1) || 0);
                        const timePercentage = performanceIndicatorsState?.total?.total?.percentage_of_year_passed || 0;
                        const annualGoalColor = annualGoalPercentage >= timePercentage ? '#ccffcc' : '#ffcccc';

                        return (
                            <tr key={index}>
                                <td>{indicator.user.firstname} {indicator.user.lastname}</td>
                                <td>{profitThisYear.toFixed(2)}</td>
                                <td style={{ backgroundColor: annualGoalColor }}>
                                    {annualGoalPercentage}%
                                </td>
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
                <p>Aastast möödunud: <strong>{timePercentage.toFixed(1)}%</strong></p>
            </>
        );
    };

    const generateCheckersIndicatorsTable = (indicators: any[]) => {
        const filteredIndicators = filterOutExcluded(indicators, EXCLUDED_CHECKERS_IDS);
        return (
            <table className="data">
                <thead>
                <tr>
                    <th style={{width: '200px'}}>Kasutaja</th>
                    <th>Kasum</th>
                    <th>% Aasta eesmärgist</th>
                </tr>
                </thead>
                <tbody>
                {filteredIndicators.map((indicator: any, index: number) => (
                    <tr key={index}>
                        <td>{indicator.user.firstname} {indicator.user.lastname}</td>
                        <td>{indicator.profit}</td>
                    </tr>
                ))}
                </tbody>
            </table>
        );
    };

    /*const generateCustomsIndicatorsTable = (indicators: any[]) => {
        return (
            <>
                <table className="data">
                    <thead>
                    <tr>
                        <th>Kasutaja</th>
                        <th>Nädala eesmärk kokku</th>
                        <th>Nädala kasum kokku</th>
                        <th>Nädala eesmärgi %</th>
                    </tr>
                    </thead>
                    <tbody>
                    {indicators.map((indicator: any, index: number) => {
                        const annualGoalPercentage = indicator.annual_goal_percentage || 0;
                        const timePercentage = performanceIndicatorsState?.total?.total?.percentage_of_year_passed || 0;
                        const annualGoalColor = annualGoalPercentage >= timePercentage ? '#ccffcc' : '#ffcccc';

                        return (
                            <tr key={index}>
                                <td>{indicator.user.firstname} {indicator.user.lastname}</td>
                                <td>{indicator.customs_order_goal.toFixed(2)}</td>
                                <td>{(indicator.customs_order_profit + indicator.customs_direct_profit).toFixed(2)}</td>
                                <td
                                    style={{
                                        backgroundColor: indicator.customs_order_profit_percentage >= 100 ? '#ccffcc' : '#ffcccc'
                                    }}
                                >
                                    {indicator.customs_order_profit_percentage.toFixed(1)}%
                                </td>
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
                <table className="data">
                    <thead>
                    <tr>
                        <th>Kasutaja</th>
                        <th>Otsetollid kasum</th>
                        <th>Logistikutele tolli kasum</th>
                        <th>% Aastane otse toll eesmärk</th>
                        <th>% Aastane tellimus toll kokku</th>
                    </tr>
                    </thead>
                    <tbody>
                    {indicators.map((indicator: any, index: number) => {
                        const directAnnualGoalPercentage = indicator.customs_direct_profit_percentage || 0;
                        const orderAnnualGoalPercentage = indicator.customs_order_profit_percentage || 0;

                        const timePercentage = performanceIndicatorsState?.total?.total?.percentage_of_year_passed || 0;

                        const directAnnualGoalColor = directAnnualGoalPercentage >= timePercentage ? '#ccffcc' : '#ffcccc';
                        const orderAnnualGoalColor = orderAnnualGoalPercentage >= timePercentage ? '#ccffcc' : '#ffcccc';

                        return (
                            <tr key={index}>
                                <td>{indicator.user.firstname} {indicator.user.lastname}</td>
                                <td>{indicator.customs_direct_profit.toFixed(2)}</td>
                                <td>{indicator.customs_order_profit.toFixed(2)}</td>
                                <td style={{ backgroundColor: directAnnualGoalColor }}>
                                    {directAnnualGoalPercentage.toFixed(1)}%
                                </td>
                                <td style={{ backgroundColor: orderAnnualGoalColor }}>
                                    {orderAnnualGoalPercentage.toFixed(1)}%
                                </td>
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            </>
        );
    }; */

    const generateCustomsIndicatorsTable = (indicators: any[]) => {
        const timePercentage = performanceIndicatorsState?.total?.total?.percentage_of_year_passed || 0;

        return (
            <>
                {indicators.map((indicator: any, index: number) => {
                    const customsDirectGoal = indicator?.customs_direct_goal || 0;
                    const customsOrderGoal = indicator?.customs_order_goal || 0;
                    const totalWeeklyGoal = customsDirectGoal + customsOrderGoal;

                    const customsDirectProfit = indicator?.customs_direct_profit || 0;
                    const customsOrderProfit = indicator?.customs_order_profit || 0;
                    const totalWeeklyProfit = customsDirectProfit + customsOrderProfit;

                    const customsDirectGoalPercentage = customsDirectGoal
                        ? ((customsDirectProfit / customsDirectGoal) * 100).toFixed(1)
                        : 0;

                    const customsOrderGoalPercentage = customsOrderGoal
                        ? ((customsOrderProfit / customsOrderGoal) * 100).toFixed(1)
                        : 0;

                    const totalWeeklyGoalPercentage = totalWeeklyGoal
                        ? ((totalWeeklyProfit / totalWeeklyGoal) * 100).toFixed(1)
                        : 0;

                    const customsDirectAnnualGoal = indicator?.customs_direct_annual_goal || 0;
                    const customsOrderAnnualGoal = indicator?.customs_order_annual_goal || 0;
                    const totalAnnualGoal = customsDirectAnnualGoal + customsOrderAnnualGoal;

                    const customsDirectAnnualProfit = indicator?.customs_direct_annual_profit || 0;
                    const customsOrderAnnualProfit = indicator?.customs_order_annual_profit || 0;
                    const totalAnnualProfit = customsDirectAnnualProfit + customsOrderAnnualProfit;

                    const customsDirectAnnualGoalPercentage = customsDirectAnnualGoal
                        ? Number(((customsDirectAnnualProfit / customsDirectAnnualGoal) * 100).toFixed(1))
                        : 0;

                    const customsOrderAnnualGoalPercentage = customsOrderAnnualGoal
                        ? Number(((customsOrderAnnualProfit / customsOrderAnnualGoal) * 100).toFixed(1))
                        : 0;

                    const totalAnnualGoalPercentage = totalAnnualGoal
                        ? Number(((totalAnnualProfit / totalAnnualGoal) * 100).toFixed(1))
                        : 0;

                    const directAnnualGoalColor =
                        customsDirectAnnualGoalPercentage >= timePercentage ? "#ccffcc" : "#ffcccc";
                    const orderAnnualGoalColor =
                        customsOrderAnnualGoalPercentage >= timePercentage ? "#ccffcc" : "#ffcccc";
                    const totalAnnualGoalColor =
                        totalAnnualGoalPercentage >= timePercentage ? "#ccffcc" : "#ffcccc";

                    return (
                        <div key={index}>
                            <h3>Kasutaja: {indicator.user.firstname} {indicator.user.lastname}</h3>
                            <table className="data">
                                <thead>
                                <tr>
                                    <th style={{ width: '300px' }}>Nädala eesmärgid</th>
                                    <th>Otsetollid</th>
                                    <th>Logistikutele</th>
                                    <th>Kokku</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr>
                                    <td>Summa</td>
                                    <td>{customsDirectGoal.toFixed(2)}</td>
                                    <td>{customsOrderGoal.toFixed(2)}</td>
                                    <td>{totalWeeklyGoal.toFixed(2)}</td>
                                </tr>
                                {indicator?.customs_direct_profit !== null && (
                                    <tr>
                                        <td>Hetkeseis</td>
                                        <td>{customsDirectProfit.toFixed(2)}</td>
                                        <td>{customsOrderProfit.toFixed(2)}</td>
                                        <td>{totalWeeklyProfit.toFixed(2)}</td>
                                    </tr>
                                )}
                                <tr>
                                    <td>% nädala eesmärgist</td>
                                    <td style={{ backgroundColor: customsDirectGoalPercentage >= 100 ? "#ccffcc" : "#ffcccc" }}>
                                        {customsDirectGoalPercentage}%
                                    </td>
                                    <td style={{ backgroundColor: customsOrderGoalPercentage >= 100 ? "#ccffcc" : "#ffcccc" }}>
                                        {customsOrderGoalPercentage}%
                                    </td>
                                    <td style={{ backgroundColor: totalWeeklyGoalPercentage >= 100 ? "#ccffcc" : "#ffcccc" }}>
                                        {totalWeeklyGoalPercentage}%
                                    </td>
                                </tr>
                                </tbody>
                            </table>

                            <table className="data">
                                <thead>
                                <tr>
                                    <th style={{ width: '300px' }}>Aasta eesmärgid</th>
                                    <th>Otsetollid</th>
                                    <th>Logistikutele</th>
                                    <th>Kokku</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr>
                                    <td>Summa</td>
                                    <td>{customsDirectAnnualGoal.toFixed(2)}</td>
                                    <td>{customsOrderAnnualGoal.toFixed(2)}</td>
                                    <td>{totalAnnualGoal.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <td>Hetkeseis</td>
                                    <td>{customsDirectAnnualProfit.toFixed(2)}</td>
                                    <td>{customsOrderAnnualProfit.toFixed(2)}</td>
                                    <td>{totalAnnualProfit.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <td>% aasta eesmärgist</td>
                                    <td style={{ backgroundColor: directAnnualGoalColor }}>
                                        {customsDirectAnnualGoalPercentage}%
                                    </td>
                                    <td style={{ backgroundColor: orderAnnualGoalColor }}>
                                        {customsOrderAnnualGoalPercentage}%
                                    </td>
                                    <td style={{ backgroundColor: totalAnnualGoalColor }}>
                                        {totalAnnualGoalPercentage}%
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                            <p>Aastast möödunud: <strong>{timePercentage.toFixed(1)}%</strong></p>
                        </div>
                    );
                })}
            </>
        );
    };

    const handleTabClick = (tab: Tab) => {
        setActiveTab(tab);
    };

    const renderTabContent = () => {
        switch (activeTab.identifier) {
            case 'total':
                return (<Totals week={week}/>);
            case 'checkers':
                return (
                    <>
                        <h2>Checkerite tulemused</h2>
                        {generateCheckersIndicatorsTable(performanceIndicatorsState?.total?.checkers ?? [])}
                    </>
                );
            case 'logisticians':
                return (
                    <>
                        <h2>Logistikute Tulemused</h2>
                        {generateIndicatorsTable(performanceIndicatorsState?.total?.logisticians ?? [])}
                    </>
                );
            case 'customs':
                return (
                    <>
                        <h2>Tollideklarantide tulemused</h2>
                        {generateCustomsIndicatorsTable(performanceIndicatorsState?.total?.customs ?? [])}
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <LoggedInView fullWidth={false}>
            <h1>Tulemusnäitajad</h1>
            <ul className="tabs clear" style={{width: '100%'}}>
                {TABS.map(tab => (
                    <li key={tab.identifier} className={activeTab === tab ? "active" : ""}>
                        <a href="Javascript:;" onClick={() => handleTabClick(tab)}>{tab.name}</a>
                    </li>
                ))}
            </ul>
            <RangeSearch url={'/results/performance'} userVisible={false}/>
            {performanceIndicatorsState.status === RequestStatus.Loading && <p>Laen...</p>}
            {renderTabContent()}
            {performanceIndicatorsState.status === RequestStatus.Failed && <p>Viga.</p>}
        </LoggedInView>
    );
};
